import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { FileText, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignContract() {
  const { contractId } = useParams();
  const [contract, setContract] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);
  
  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  // Form data
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");

  useEffect(() => {
    fetchContract();
  }, [contractId]);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#1A1A1A";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
    }
  }, [contract]);

  const fetchContract = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/${contractId}/public`);
      if (response.ok) {
        const data = await response.json();
        setContract(data.contract);
        setProjectName(data.project_name);
        setClientName(data.client_name);
        setSignerName(data.client_name);
      } else {
        const err = await response.json();
        setError(err.detail || "Contract not found");
      }
    } catch (error) {
      setError("Failed to load contract");
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing handlers
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#1A1A1A";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSign = async () => {
    if (!signerName || !signerEmail) {
      toast.error("Please enter your name and email");
      return;
    }
    if (!hasSignature) {
      toast.error("Please sign in the box above");
      return;
    }

    setSigning(true);

    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL("image/png");

      const response = await fetch(`${API_URL}/api/contracts/${contractId}/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signature_data: signatureData,
          signed_by_name: signerName,
          signed_by_email: signerEmail
        })
      });

      if (response.ok) {
        setSigned(true);
        toast.success("Contract signed successfully!");
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to sign contract");
      }
    } catch (error) {
      toast.error("Error signing contract");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-[#FF6B35] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Card className="bg-[#1A1A1A] border-[#FF4444] max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-[#FF4444] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Unable to Load Contract</h2>
            <p className="text-[#888888]">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Card className="bg-[#1A1A1A] border-[#4CAF50] max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="h-16 w-16 bg-[#4CAF50]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-[#4CAF50]" />
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2">Contract Signed!</h2>
            <p className="text-[#888888] mb-4">
              Thank you for signing. A confirmation email has been sent to {signerEmail}.
            </p>
            <p className="text-[#FF6B35] text-sm font-medium">TAT. ATELIER</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-[#FF6B35] font-semibold tracking-widest text-sm mb-2">TAT. ATELIER</p>
          <h1 className="text-2xl font-semibold text-white">Digital Signature Request</h1>
        </div>

        {/* Contract Card */}
        <Card className="bg-[#1A1A1A] border-[#262626] mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded bg-[#262626] flex items-center justify-center">
                <FileText className="h-6 w-6 text-[#FF6B35]" />
              </div>
              <div>
                <CardTitle className="text-lg text-white">{contract.title}</CardTitle>
                <p className="text-[#888888] text-sm">Project: {projectName}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-[#111111] rounded p-4 max-h-[400px] overflow-y-auto mb-4">
              <pre className="text-[#CCCCCC] text-sm whitespace-pre-wrap font-sans leading-relaxed">
                {contract.content}
              </pre>
            </div>
            {contract.valid_until && (
              <p className="text-[#888888] text-xs">Valid until: {contract.valid_until}</p>
            )}
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card className="bg-[#1A1A1A] border-[#262626]">
          <CardHeader>
            <CardTitle className="text-lg text-white">Sign Below</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#888888] mb-1 block">Full Name *</label>
                <Input
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Enter your full name"
                  className="bg-[#111111] border-[#262626]"
                />
              </div>
              <div>
                <label className="text-sm text-[#888888] mb-1 block">Email *</label>
                <Input
                  type="email"
                  value={signerEmail}
                  onChange={(e) => setSignerEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="bg-[#111111] border-[#262626]"
                />
              </div>
            </div>

            {/* Signature Canvas */}
            <div>
              <label className="text-sm text-[#888888] mb-2 block">Your Signature *</label>
              <div className="border border-[#262626] rounded overflow-hidden">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={150}
                  className="w-full cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              <div className="flex justify-end mt-2">
                <Button variant="ghost" size="sm" onClick={clearSignature} className="text-[#888888]">
                  Clear Signature
                </Button>
              </div>
            </div>

            <p className="text-[#888888] text-xs">
              By signing, you agree to the terms and conditions outlined in this contract.
              Your signature and IP address will be recorded for verification purposes.
            </p>

            <Button
              onClick={handleSign}
              disabled={signing || !hasSignature}
              className="w-full bg-[#FF6B35] hover:bg-[#FF6B35]/90 h-12"
            >
              {signing ? "Signing..." : "Sign Contract"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
